import React from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

function Home() {
  return (
    <div className="bg-transparent text-white min-h-screen">
      {/* Hero Section with Checkered Flag */}
      <section 
        className="racing-hero py-20 relative overflow-hidden min-h-[600px] flex items-center"
        style={{
          backgroundImage: 'url(/images/flag.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="max-w-4xl mx-auto text-center px-4 racing-content">
          <div className="mb-8">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-2xl">
              Welcome to F1 Predictions
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 font-light mb-8 drop-shadow-lg">
              Predict race outcomes and compete with friends throughout the Formula 1 season!
            </p>
          </div>
          
          {/* Status Alert */}
          <div className="bg-yellow-900 bg-opacity-90 border border-yellow-600 rounded-xl p-6 mb-8 backdrop-blur-sm shadow-2xl">
            <div className="flex items-center justify-center space-x-3 mb-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-lg font-semibold text-yellow-200">2026 Season Live</h3>
            </div>
            <div className="space-y-2 text-yellow-100">
              <p>New 2026 scoring model is live!</p>
              <p>If there are any errors, please reach out to <span className="font-medium text-yellow-50">f1predictor.club@gmail.com</span></p>
            </div>
          </div>

          {/* Quick Action Button */}
          <div className="flex justify-center">
            <Link 
              to="/2026/predictions" 
              className="bg-gray-700 hover:bg-gray-600 border-2 border-red-500 hover:border-red-400 px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 shadow-2xl inline-flex items-center space-x-2 text-white hover:text-red-100"
            >
              <span className="text-2xl">🏁</span>
              <span>Enter My Predictions</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-transparent">
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* How It Works */}
          <div className="bg-gray-800 rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-300 border border-gray-700 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">⚡</span>
              </div>
              <h2 className="text-2xl font-bold text-white">How It Works</h2>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">1</div>
                <p className="text-gray-300">Create an account to join the competition</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">2</div>
                <p className="text-gray-300">Submit your predictions before each race</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">3</div>
                <p className="text-gray-300">Earn points for correct predictions</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">4</div>
                <p className="text-gray-300">Track your performance on the leaderboard</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-xs font-bold mt-1">5</div>
                <p className="text-gray-300">Compete throughout the entire F1 season</p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-blue-900 bg-opacity-50 rounded-lg border border-blue-700">
              <p className="text-blue-200 text-sm">
                <span className="font-semibold">Note:</span> 2026 scoring based on feature races. Sprint races available with reduced points.
              </p>
            </div>
          </div>

          {/* Prediction Categories */}
          <div className="bg-gray-800 rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-300 border border-gray-700 shadow-xl">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h2 className="text-2xl font-bold text-white">Prediction Categories</h2>
            </div>
            
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🏆</span>
                  <span className="font-medium">Top 5 Positions (P1-P5) and earn points to P10</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">⚡</span>
                  <span className="font-medium">Pole Position</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🏁</span>
                  <span className="font-medium">Fastest Lap</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🚩</span>
                  <span className="font-medium">First Retirement</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🚀</span>
                  <span className="font-medium">Most Places Gained</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-gray-700 to-gray-600 rounded-lg p-4 hover:from-red-800 hover:to-red-700 transition-all duration-300 border border-gray-600 hover:border-red-500">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">👥</span>
                  <span className="font-medium">Head-to-Head Battles (Driver & Team)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-red-900 to-red-800 rounded-2xl p-8 text-center border border-red-600 shadow-2xl mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
              <span className="text-3xl">⏰</span>
            </div>
          </div>
          <h3 className="text-2xl font-bold mb-4 text-white">Prediction Deadline</h3>
          <p className="text-xl text-red-100 mb-4">
            Predictions close <span className="font-bold text-white">2 minutes before qualifying starts</span>
          </p>
        </div>

        {/* Points Breakdown - 2026 SCORING */}
        <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-xl">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center mr-4">
              <span className="text-2xl">📊</span>
            </div>
            <h2 className="text-3xl font-bold text-white">2026 Scoring</h2>
          </div>
          
          <div className="bg-blue-900 bg-opacity-50 p-4 rounded-lg border border-blue-600 mb-8">
            <p className="text-blue-200">
              <strong>💡 How Scoring Works:</strong> You earn the F1 championship points your selected driver actually earns. You get double points for correct position. All 3 podium correct = triple points on podium. All 5 positions correct = 4× the base score (320 points).
            </p>
          </div>
          
          <div className="space-y-8">
            {/* Position Points */}
            <div className="bg-gray-700 rounded-xl p-6 border border-gray-600">
              <h3 className="font-bold text-xl mb-4 text-blue-300 flex items-center">
                <span className="text-2xl mr-2">🏆</span>
                Position Points (Feature Races)
              </h3>
              <div className="bg-gray-600 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-10 gap-2 mb-3">
                  {['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'].map(pos => (
                    <div key={pos} className="text-center">
                      <div className="text-xs font-semibold text-gray-300">{pos}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-10 gap-2">
                  {[25, 18, 15, 12, 10, 8, 6, 4, 2, 1].map((pts, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-xl font-bold text-yellow-400">{pts}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-sm text-gray-300 p-3 bg-gray-600 rounded">
                <em>Base points earned when driver finishes in that position. Double points if you selected the correct position.</em>
              </div>
            </div>

            {/* Bonuses */}
            <div className="bg-green-900 bg-opacity-40 rounded-xl p-6 border border-green-500">
              <h3 className="font-bold text-xl mb-4 text-green-300 flex items-center">
                <span className="text-2xl mr-2">✨</span>
                Position Bonuses
              </h3>
              <div className="space-y-4">
                <div className="bg-gray-700 rounded-lg p-4 border-l-4 border-green-500">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center font-bold text-white">×2</div>
                    <strong className="text-green-300">Correct Position</strong>
                  </div>
                  <p className="text-gray-300 text-sm ml-11">You get double points when your predicted driver finishes in the position you selected</p>
                  <p className="text-yellow-400 text-sm ml-11 mt-1"><em>Example: Predict P1 and driver finishes P1 = 25 × 2 = 50 points</em></p>
                </div>
                
                <div className="bg-gray-700 rounded-lg p-4 border-l-4 border-purple-500">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center font-bold text-white">×3</div>
                    <strong className="text-purple-300">All 3 Podium Correct (P1-P3)</strong>
                  </div>
                  <p className="text-gray-300 text-sm ml-11">You get triple points on podium when all 3 are correct</p>
                  <p className="text-yellow-400 text-sm ml-11 mt-1"><em>Example: P1 (50) + P2 (36) + P3 (30) + 58 bonus = 174 points</em></p>
                </div>

                <div className="bg-gray-700 rounded-lg p-4 border-l-4 border-orange-500">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center font-bold text-white">×4</div>
                    <strong className="text-orange-300">All 5 Positions Correct (P1-P5)</strong>
                  </div>
                  <p className="text-gray-300 text-sm ml-11">You get 4× the base top 5 score</p>
                  <p className="text-yellow-400 text-sm ml-11 mt-1"><em>Example: Base 80 points → All 5 correct = 320 points</em></p>
                </div>
              </div>
            </div>

            {/* Other Predictions */}
            <div className="bg-orange-900 bg-opacity-40 rounded-xl p-6 border border-orange-500">
              <h3 className="font-bold text-xl mb-4 text-orange-300 flex items-center">
                <span className="text-2xl mr-2">⚡</span>
                Other Predictions (Fixed Points)
              </h3>
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
                <p className="text-orange-200 font-semibold mb-3">Feature Races: <strong>4 points each</strong></p>
                <p className="text-orange-200 font-semibold mb-3">Sprint Races: <strong>1 point each</strong></p>
                <p className="text-gray-300 text-sm">Applies to: Pole Position, Fastest Lap, First Retirement, Driver Head-to-Head, Team Head-to-Head</p>
              </div>
            </div>

            {/* Most Places Gained */}
            <div className="bg-green-900 bg-opacity-40 rounded-xl p-6 border border-green-500">
              <h3 className="font-bold text-xl mb-4 text-green-300 flex items-center">
                <span className="text-2xl mr-2">🚀</span>
                Most Places Gained
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                  <div>
                    <strong className="text-green-300">Correct Prediction:</strong>
                    <span className="text-gray-300 ml-2">1 point</span>
                  </div>
                </div>
                <div className="text-sm text-green-300 mt-3 p-3 bg-gray-700 rounded space-y-1">
                  <p><em>Select the driver who gains the most positions from grid to finish</em></p>
                  <p><em>If multiple drivers tie, all users who predicted any tied driver get the point</em></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Home;